import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import throttle from 'lodash/throttle';
import styled, { css } from 'styled-components';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryTooltip } from 'victory';
import { theme, toRem } from 'design';
import { sumBy } from 'utils/arrays';

interface IChartProps {
  data: any;
  className?: string;
  isLoading?: boolean;
}

export const ChartContainer = styled.div<Partial<IChartProps>>`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;

  ${({ isLoading }) =>
    isLoading &&
    css`
      > span {
        width: 100%;
        height: ${toRem(300)};
      }
    `}

  svg {
    * {
      transition: fill ease-out 250ms;
    }
  }

  div.VictoryContainer {
    touch-action: auto !important; // Allow touch events on the chart
  }
`;

const ChartLoader = styled(Skeleton)`
  && {
    width: 100%;
    height: ${toRem(300)};
  }
`;

const CHART_HEIGHT = 300;
const COLOR_NOTHOVER = theme.color.primary[25];
const COLOR_DEFAULT = theme.color.primary[500];

export const BarChart = ({ data, className, isLoading }: IChartProps) => {
  // Parent Element Width to Resize SVG Chart
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const parentElementClassName = 'chartContainer';

  useEffect(() => {
    const resize = () => {
      const layout = document.querySelector(`.${parentElementClassName}`);

      if (layout) {
        const width = layout.clientWidth;
        setContainerWidth(width);
      }
    };

    // 16.6 represents 16.6ms, e.g. ~1/60th of a second for 60fps rendering
    const throttledResize = throttle(resize, 16.6);
    resize();
    window.addEventListener('resize', throttledResize);

    return () => {
      window.removeEventListener('resize', throttledResize);
    };
  }, []);

  const dl = data.length;

  const displayHorizontalTickLabels = () => {
    if (dl >= 15 && containerWidth <= 550) return false;
    if (dl >= 10 && containerWidth <= 450) return false;
    return true;
  };

  const getDomainPaddingX = () => {
    if (dl >= 15 && containerWidth <= 550) return 11;
    if (dl >= 10 && containerWidth <= 550) return 60;
    return 60;
  };

  const formatCurrency = (n: number, excludeDecimal?: boolean) => {
    const fixedDecimals = excludeDecimal && Number.isInteger(n) ? 0 : 2;
    return `$${n.toFixed(fixedDecimals).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
  };

  const displayVerticalTickLabels = () => {
    const yValues = dl && data.map((d: any) => d.y);

    return yValues;
  };

  const totalYAmount = sumBy(data, 'y');

  return (
    <ChartContainer isLoading={isLoading} className={`${parentElementClassName} ${className}`}>
      {isLoading || !containerWidth ? (
        <ChartLoader />
      ) : (
        <VictoryChart
          width={containerWidth}
          height={CHART_HEIGHT}
          padding={{
            top: 5.5,
            right: 0,
            bottom: displayHorizontalTickLabels() ? 22 : 0,
            left: displayVerticalTickLabels() ? 58 : 0,
          }}
          domainPadding={{
            x: [getDomainPaddingX(), getDomainPaddingX()],
            y: [0, 20],
          }}
        >
          <defs>
            <filter id="filterShadow" x="0.75" y="0" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="4.5288" />
              <feGaussianBlur stdDeviation="3.12487" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="20.15" />
              <feGaussianBlur stdDeviation="20.8015" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
              <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="100" />
              <feGaussianBlur stdDeviation="69" />
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.0348411 0" />
              <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow" />
              <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow" result="shape" />
            </filter>
          </defs>

          <VictoryAxis
            width={containerWidth}
            fixLabelOverlap
            style={{
              axis: { strokeWidth: 1, stroke: theme.color.neutral[200] },
              tickLabels: {
                fontFamily: theme.font.family.euclid,
                fontWeight: theme.font.weight.medium,
                fontSize: 11,
                fill: theme.color.neutral[400],
                opacity: displayHorizontalTickLabels() ? 1 : 0,
              },
            }}
          />

          <VictoryAxis
            dependentAxis
            height={CHART_HEIGHT}
            tickFormat={(t) => `${formatCurrency(t, true)}`}
            style={{
              axis: { strokeWidth: 0, stroke: theme.color.neutral[100] },
              grid: { strokeWidth: 1, stroke: theme.color.neutral[100] },
              tickLabels: {
                fontFamily: theme.font.family.euclid,
                fontWeight: theme.font.weight.medium,
                fontSize: 11,
                color: theme.color.neutral[400],
                fill: theme.color.neutral[400],
                opacity: displayVerticalTickLabels() ? 1 : 0,
              },
            }}
            {...(totalYAmount < 1 && { tickValues: [0, totalYAmount] })}
          />

          <VictoryBar
            width={containerWidth}
            height={CHART_HEIGHT}
            alignment="middle"
            barRatio={0.66}
            cornerRadius={{
              topLeft: 2.75,
              topRight: 2.75,
            }}
            style={{
              data: {
                fill: theme.color.primary[500],
              },
              parent: { overflow: 'visible' },
            }}
            labels={({ datum }) => [`Instant: ${formatCurrency(datum.instant)}`, `Payroll: ${formatCurrency(datum.payroll)}`, `Cash: ${formatCurrency(datum.cash)}`, `Total: ${formatCurrency(datum.y)}`]}
            labelComponent={
              <VictoryTooltip
                constrainToVisibleArea
                cornerRadius={11}
                flyoutWidth={132}
                flyoutHeight={55}
                dy={-5.5}
                pointerLength={0}
                flyoutStyle={{
                  paddingRight: theme.space.sm,
                  paddingLeft: theme.space.sm,
                  fill: theme.color.neutral[900],
                  filter: 'url(#filterShadow)',
                  strokeWidth: 0,
                }}
                style={{
                  fontFamily: theme.font.family.euclid,
                  fontWeight: theme.font.weight.semiBold,
                  fontSize: 11,
                  lineHeight: 2,
                  fill: theme.color.white,
                  textAlign: 'center',
                }}
              />
            }
            events={[
              {
                target: 'data',
                eventHandlers: {
                  // Change fill color of datum
                  onMouseEnter: () => [
                    {
                      // Change all bars fill
                      eventKey: 'all',
                      target: 'data',
                      mutation: () => ({
                        style: {
                          fill: COLOR_NOTHOVER,
                        },
                      }),
                    },
                    {
                      // Change fill of active datum
                      target: 'data',
                      mutation: () => ({
                        style: {
                          fill: COLOR_DEFAULT,
                        },
                      }),
                    },
                  ],
                  onMouseLeave: () => [
                    {
                      // Change fill of all back to default
                      eventKey: 'all',
                      target: 'data',
                      mutation: () => ({
                        style: {
                          fill: COLOR_DEFAULT,
                        },
                      }),
                    },
                    {
                      // Change fill of previously active datum back to default
                      target: 'data',
                      mutation: () => ({
                        style: {
                          fill: COLOR_DEFAULT,
                        },
                      }),
                    },
                  ],
                },
              },
            ]}
            data={data}
            {...((data.length === 1 || (data.length <= 5 && containerWidth > 550) || (data.length <= 20 && containerWidth > 2200)) && { barWidth: 100 })}
          />
        </VictoryChart>
      )}
    </ChartContainer>
  );
};
